'use client';

import { SNACKBAR_VARIANT } from '@/constants/notifications';
import type { HTMLAttributes, Key, MouseEvent } from 'react';
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SearchIcon } from '@/components/icons';
import { useLocalStorageState } from '@/hooks/common/useLocalStorageState';
import { Chip } from '@/primitives';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FilterOptionsState,
  TextField,
} from '@mui/material';
import { keyframes } from '@emotion/react';
import { useRouter } from 'next/navigation';
import { useGetSocietiesLazyQuery } from '@/publicSearch/graphql/queries.generated';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import usePublicSearchContext, {
  Society,
} from '@/publicSearch/store/usePublicSearchContext';
import { enqueueSnackbar } from '@/providers/SnackbarProvider';
import { alpha } from '@mui/material/styles';
import { black, blue, white } from '@/lib/colours';
import { RECENTLY_VIEWED_ORGS } from '@/configs/storageKeys';
import {
  clearSelectedOrganisation,
  storeSelectedOrganisation,
} from '@/utils/storage';
import { BackgroundVideo } from '../BackgroundVideo';

export type Option = {
  empty?: boolean;
} & Society;

const glowSpin = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
`;

const MAX_RECENTLY_VIEWED_ORG_OPTIONS = 5;
const MAX_ORG_OPTIONS = 10;

export default function HeroSection() {
  const setSociety = usePublicSearchContext((context) => context.setSociety);
  const [recentlyViewedOrgs, setRecentlyViewedOrgs] = useLocalStorageState<
    Option[]
  >([], RECENTLY_VIEWED_ORGS);

  const [searchValue, setSearchValue] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);
  const [organisations, setOrganisations] = useState<Option[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSearchValueRef = useRef<string>('');
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [
    searchSocieties,
    { data: societiesData, loading: isLoadingSocieties },
  ] = useGetSocietiesLazyQuery();

  const router = useRouter();

  // Stabilize the search function to prevent constant re-renders
  const stableSearchSocieties = useCallback(
    (searchTerm: string) => {
      searchSocieties({
        variables: {
          currentPage: 1,
          pageSize: 10,
          searchTerm,
        },
      });
    },
    [searchSocieties],
  );

  const handleOpenPopper = useCallback(() => {
    setOpen(true);
  }, []);

  const addRecentlyViewedOrg = useCallback(
    (option: Option) => {
      if (option && option.orgId) {
        setRecentlyViewedOrgs((prev) => {
          const filtered = prev.filter(
            (existingOrg) => existingOrg.orgId !== option.orgId,
          );
          return [option, ...filtered].slice(
            0,
            MAX_RECENTLY_VIEWED_ORG_OPTIONS,
          );
        });
      }
    },
    [setRecentlyViewedOrgs],
  );

  const handleSelectOrg = useCallback(
    (org: Option) => {
      const code = org.societyCode?.trim();
      if (!code || !org.orgId) {
        enqueueSnackbar('Organisation not found', {
          variant: SNACKBAR_VARIANT.ERROR,
        });
        return;
      }

      addRecentlyViewedOrg(org);
      setSociety(org);
      // SELECTED_ORG_ID feeds the registry (core) backend via X-Organisation-Id,
      // which is keyed off the BreedLogic organisation id, not the RPT societyId.
      // For i4-sourced societies (no breedLogicSocietyId) the registry can't
      // resolve them, so clear the slot to avoid leaking a previously-selected
      // BL org id into core requests made from an i4 society context.
      if (org.breedLogicSocietyId != null) {
        storeSelectedOrganisation(
          Number(org.breedLogicSocietyId),
          org.name?.trim() || code,
        );
      } else {
        clearSelectedOrganisation();
      }
      router.push(`/${code}/${ROUTE_SEGMENT.ANIMALS}`);
      setOpen(false);
      setKeepOpen(false);
    },
    [router, addRecentlyViewedOrg, setSociety],
  );

  const handleChangeOrg = useCallback(
    (_event: SyntheticEvent, newValue: string | Option | null) => {
      if (!newValue) return;

      // With freeSolo, MUI can pass a plain string (typed label) instead of the
      // option object — resolve back to an option so navigation still runs.
      let org: Option | null;
      if (typeof newValue === 'string') {
        const t = newValue.trim();
        const norm = (s: string | undefined) => s?.trim() ?? '';
        const lower = t.toLowerCase();
        const byExact = organisations.find(
          (o) =>
            !o.empty &&
            (norm(o.name) === t ||
              norm(o.societyCode) === t ||
              norm(o.name).toLowerCase() === lower ||
              norm(o.societyCode).toLowerCase() === lower),
        );
        org =
          byExact ??
          organisations.find(
            (o) =>
              !o.empty &&
              (norm(o.name).toLowerCase() === lower ||
                norm(o.societyCode).toLowerCase() === lower),
          ) ??
          null;
      } else {
        org = newValue;
      }

      if (!org || org.empty) {
        if (typeof newValue === 'string' && newValue.trim()) {
          enqueueSnackbar('Could not open that organisation. Try again.', {
            variant: SNACKBAR_VARIANT.WARNING,
          });
        }
        return;
      }
      handleSelectOrg(org);
    },
    [handleSelectOrg, organisations],
  );

  const getOptionLabel = useCallback((option: string | Option) => {
    return typeof option === 'string' ? option : option.name || '';
  }, []);

  const getOptionKey = useCallback((option: string | Option) => {
    return typeof option === 'string' ? option : option.orgId || '';
  }, []);

  const isOptionEqualToValue = useCallback(
    (option: string | Option, value: string | Option) => {
      if (typeof option === 'string' || typeof value === 'string') {
        return option === value;
      }
      return option.orgId === value.orgId;
    },
    [],
  );

  const renderAutocompleteOption = useCallback(
    (props: HTMLAttributes<HTMLLIElement> & { key: Key }, option: Option) => {
      const { key, ...optionProps } = props;
      if (option.empty) {
        return (
          <Box key={key} component="li" {...optionProps}>
            No matching results
          </Box>
        );
      }
      return (
        <Box
          key={key}
          component="li"
          {...optionProps}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Box component="span" sx={{ flex: 1 }}>
            {option.name}
          </Box>
          {option.isPrivate ? (
            <Chip label="Private" size="small" variant="outlined" />
          ) : null}
        </Box>
      );
    },
    [],
  );

  const getOptionDisabled = useCallback((option: Option) => !!option.empty, []);

  const renderAutocompleteInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      return (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder={
            isLoadingSocieties
              ? 'Loading...'
              : 'Search for a breed society or association'
          }
          sx={{
            paddingY: '1rem',
            borderRadius: '28px',
            '& input::placeholder': {
              color: alpha(white[50], 0.7),
              opacity: 1,
            },
          }}
          variant="standard"
          autoComplete="off"
          spellCheck={false}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              disableUnderline: true,
              endAdornment: isLoadingSocieties && (
                <CircularProgress
                  size={18}
                  sx={{ mr: '12px' }}
                  aria-label="Loading search results"
                />
              ),
              className: 'search-input',
              startAdornment: (
                <>
                  <SearchIcon
                    className="search-icon"
                    sx={{
                      position: 'absolute',
                      left: '10px',
                      color: 'common.white',
                      fontSize: '1.25rem',
                      width: '1.25rem',
                      height: '1.25rem',
                    }}
                  />
                </>
              ),
              sx: {
                paddingLeft: '40px',
                color: 'common.white',
              },
            },
          }}
        />
      );
    },
    [isLoadingSocieties],
  );

  const autocompleteSlotProps = useMemo(
    () => ({
      popper: {
        sx: { zIndex: 1400 },
      },
      listbox: {
        onMouseDown: (e: MouseEvent) => {
          // Keeps focus on the input so the option click completes (blur race).
          e.preventDefault();
        },
      },
      paper: {
        sx: {
          mt: 3,
          borderRadius: '8px',
          backgroundColor: alpha(black[900], 0.35),
          backdropFilter: 'blur(12px)',
          color: 'common.white',
          '& .MuiAutocomplete-option': {
            borderBottom: `1px solid ${alpha(white[50], 0.15)}`,
            '&:last-of-type': {
              borderBottom: 'none',
            },
            '&:hover, &[aria-selected="true"], &.Mui-focused': {
              backgroundColor: alpha(white[50], 0.15),
            },
          },
          '& .MuiAutocomplete-noOptions': {
            color: alpha(white[50], 0.7),
          },
        },
      },
    }),
    [],
  );

  const handleRemoveRecentlyViewedOrg = useCallback(
    (option: Option) => {
      setRecentlyViewedOrgs((prev) =>
        prev.filter((existingOrg) => existingOrg.orgId !== option.orgId),
      );
    },
    [setRecentlyViewedOrgs],
  );

  // Hide private societies from recently viewed unless the user can still resolve
  // them via search (linked users see private societies in picker results).
  const visibleRecentlyViewedOrgs = useMemo(() => {
    const accessiblePrivateIds = new Set(
      organisations.filter((o) => o.isPrivate).map((o) => o.orgId),
    );
    return recentlyViewedOrgs.filter(
      (org) => !org.isPrivate || accessiblePrivateIds.has(org.orgId),
    );
  }, [recentlyViewedOrgs, organisations]);

  const handleInputChange = useCallback(
    (_event: SyntheticEvent, value: string) => {
      // Update the search value immediately for display - no async wrapping
      setSearchValue(value);
      lastSearchValueRef.current = value;

      // Keep dropdown open while user is typing
      setKeepOpen(true);
      if (!open) {
        setOpen(true);
      }

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for search
      searchTimeoutRef.current = setTimeout(() => {
        const trimmedValue = value.trim();
        if (trimmedValue.length > 0) {
          stableSearchSocieties(trimmedValue);
        } else {
          setOrganisations([]);
        }
        // Allow closing after search is done
        setKeepOpen(false);
      }, 300);
    },
    [stableSearchSocieties, open],
  );

  const handleFilterOptions = useCallback(
    (options: Option[], state: FilterOptionsState<Option>) => {
      if (!state.inputValue.trim()) {
        return [];
      }
      const filtered = options.slice(0, MAX_ORG_OPTIONS);
      if (filtered.length === 0) {
        return [
          {
            empty: true,
            loading: false,
            name: 'empty',
            orgId: '',
          } as Option,
        ];
      }
      return filtered;
    },
    [],
  );

  useEffect(() => {
    if (!societiesData?.societies?.items.length) return;
    setOrganisations(
      (societiesData.societies.items || []).map(
        (society) =>
          ({
            ...society,
            name: society.name || '',
            orgId: String(society.societyId || ''),
            societyCode: society.societyCode || '',
          }) as Option,
      ),
    );
  }, [societiesData]);

  // Simple cleanup on unmount
  useEffect(() => {
    return () => {
      const searchTimeout = searchTimeoutRef.current;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const focusTimeout = focusTimeoutRef.current;
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      if (focusTimeout) {
        clearTimeout(focusTimeout);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    >
      <BackgroundVideo />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          px: '20px',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '500px',
            position: 'relative',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: '-4px',
              borderRadius: '12px',
              overflow: 'hidden',
              zIndex: 0,
              pointerEvents: 'none',
              filter: 'blur(3px)',
              mask: `linear-gradient(${white[50]} 0 0) content-box, linear-gradient(${white[50]} 0 0)`,
              maskComposite: 'exclude',
              WebkitMaskComposite: 'xor',
              padding: '4px',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '200%',
                aspectRatio: '1',
                background: `conic-gradient(from 0deg, ${blue[500]}, ${blue[400]}, ${alpha(blue[300], 0.5)}, transparent 30%, transparent 70%, ${alpha(blue[300], 0.5)}, ${blue[400]}, ${blue[500]})`,
                animation: `${glowSpin} 3s linear infinite`,
              }}
            />
          </Box>
          <Autocomplete
            key="breed-search-autocomplete"
            disablePortal
            options={organisations}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            isOptionEqualToValue={isOptionEqualToValue}
            onOpen={handleOpenPopper}
            onChange={handleChangeOrg}
            sx={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: '500px',
              backgroundColor: alpha(black[900], 0.35),
              backdropFilter: 'blur(12px)',
              borderRadius: '8px',
            }}
            inputValue={searchValue}
            freeSolo
            onInputChange={handleInputChange}
            open={open}
            onClose={(_event, reason) => {
              if (keepOpen && reason !== 'blur') {
                return;
              }
              setOpen(false);
            }}
            blurOnSelect={false}
            clearOnBlur={false}
            disableClearable
            slotProps={autocompleteSlotProps}
            filterOptions={handleFilterOptions}
            renderOption={renderAutocompleteOption}
            getOptionDisabled={getOptionDisabled}
            renderInput={renderAutocompleteInput}
          />
        </Box>
        {visibleRecentlyViewedOrgs.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1.5,
              justifyContent: 'center',
              alignItems: 'center',
              maxWidth: '500px',
              width: '100%',
              opacity: open && searchValue.trim().length > 0 ? 0 : 1,
              transition: 'opacity 0.3s ease',
              pointerEvents:
                open && searchValue.trim().length > 0 ? 'none' : 'auto',
            }}
          >
            {visibleRecentlyViewedOrgs.map((org) => (
              <Chip
                key={org.orgId}
                label={org.name}
                size="small"
                onClick={() => handleSelectOrg(org)}
                onDelete={() => handleRemoveRecentlyViewedOrg(org)}
                sx={{
                  backgroundColor: alpha(white[50], 0.15),
                  color: 'common.white',
                  '&:hover': {
                    backgroundColor: alpha(white[50], 0.25),
                  },
                  '& .MuiChip-deleteIcon': {
                    color: alpha(white[50], 0.5),
                    '&:hover': {
                      color: 'common.white',
                    },
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
